import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from 'components/ui/use-toast';
import { saveQuestionReport } from './api';
import type { AttemptQuestionReports, QuestionReportInput } from './types';
import { attemptQuestionReportsKey } from './useAttemptQuestionReports';

/** Sends (or replaces) a report and puts the saved copy straight into the attempt's cached list. */
export function useSaveQuestionReport(attemptId: string, questionId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: QuestionReportInput) => saveQuestionReport(attemptId, questionId, input),
    onSuccess: (saved) => {
      queryClient.setQueryData<AttemptQuestionReports>(attemptQuestionReportsKey(attemptId), (previous) => ({
        enabled: previous?.enabled ?? true,
        reports: [...(previous?.reports ?? []).filter((report) => report.questionId !== saved.questionId), saved],
      }));
      toast.success(t('question_reports.sent_title'), { description: t('question_reports.sent_description') });
    },
    onError: () => {
      toast.error(t('question_reports.save_error_title'), { description: t('question_reports.save_error_description') });
    },
  });
}
